import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Star, TrendingUp, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const projects = [
  {
    companyName: "bewerbenautomatisch.ch",
    tagline: "Der Stellenmarkt, der die Firmen selbst durchsucht",
    description: "Ein eigener Crawler durchsucht laufend Schweizer Firmenwebseiten nach offenen Stellen, bereitet sie automatisch auf und veröffentlicht sie als Stellenmarkt – täglich aktualisiert, ohne manuelle Pflege.",
    results: [
      { label: "Offene Stellen", value: "330+", icon: <Star className="w-4 h-4" /> },
      { label: "Unternehmen", value: "195", icon: <TrendingUp className="w-4 h-4" /> },
      { label: "Aktualisierung", value: "täglich", icon: <Clock className="w-4 h-4" /> }
    ],
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop",
    gradient: "from-slate-800 to-neutral-600",
    tech: ["React", "Python", "Supabase", "Netlify"],
    websiteUrl: "https://bewerbenautomatisch.ch"
  },
  {
    companyName: "Wolf Haustechnik",
    tagline: "KI-Revolution im Handwerk",
    description: "Transformation eines traditionellen Handwerksbetriebs in ein digitales Powerhouse. Mit KI-gestützter App und automatisierten Prozessen.",
    results: [
      { label: "Zeitersparnis", value: "3h/Tag", icon: <Clock className="w-4 h-4" /> },
      { label: "Effizienz", value: "+85%", icon: <TrendingUp className="w-4 h-4" /> },
      { label: "ROI", value: "12 Monate", icon: <Star className="w-4 h-4" /> }
    ],
    image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop",
    gradient: "from-blue-600 to-purple-600",
    tech: ["React Native", "Node.js", "OpenAI", "PostgreSQL"],
    websiteUrl: "https://wolfht.ch"
  },
  {
    companyName: "JobRadar",
    tagline: "Dein Traumjob findet dich",
    description: "Revolutionäre Job-Matching-App mit KI-Algorithmus. Präzision trifft auf Geschwindigkeit.",
    results: [
      { label: "Genauigkeit", value: "94%", icon: <Star className="w-4 h-4" /> },
      { label: "Vermittlung", value: "5 Tage", icon: <Clock className="w-4 h-4" /> },
      { label: "User", value: "50k+", icon: <TrendingUp className="w-4 h-4" /> }
    ],
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=600&fit=crop",
    gradient: "from-green-600 to-emerald-600",
    tech: ["Flutter", "TensorFlow", "Firebase", "Dart"],
    websiteUrl: "https://www.house-of-skills.ch/#"
  },
  {
    companyName: "ADHD-Time",
    tagline: "Zeit neu gedacht für ADHS",
    description: "Innovative Zeitmanagement-App speziell für Menschen mit ADHS. KI-gestützte Tagesplanung und Fokus-Optimierung.",
    results: [
      { label: "Produktivität", value: "+120%", icon: <TrendingUp className="w-4 h-4" /> },
      { label: "Fokuszeit", value: "2x mehr", icon: <Clock className="w-4 h-4" /> },
      { label: "Zufriedenheit", value: "95%", icon: <Star className="w-4 h-4" /> }
    ],
    image: "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?w=800&h=600&fit=crop",
    gradient: "from-purple-600 to-pink-600",
    tech: ["React", "Node.js", "AI/ML", "MongoDB"],
    websiteUrl: "https://adhd-time.ch"
  }
];

const Projects: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? projects.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === projects.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 8000);
    
    return () => clearInterval(interval);
  }, [activeIndex]);

  const currentProject = projects[activeIndex];

  return (
    <section id="projects" className="py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/30 to-blue-100/30"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center px-6 py-2 mb-6 text-sm font-semibold text-blue-700 bg-blue-100 rounded-full"
          >
            <Star className="w-4 h-4 mr-2" />
            PREMIUM CASE STUDIES
          </motion.div>
          
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block text-gray-900">Erfolge,</span>
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
              die begeistern.
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            Echte Projekte. Messbare Resultate. Begeisterte Kunden.
          </p>
        </motion.div>
        
        <motion.div 
          ref={ref}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
          className="relative max-w-6xl mx-auto"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="grid lg:grid-cols-2">
                <div className="relative h-96 lg:h-auto">
                  <img 
                    src={currentProject.image} 
                    alt={currentProject.companyName} 
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${currentProject.gradient} opacity-20`}></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
                    <div className="flex flex-wrap gap-2">
                      {currentProject.tech.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white/20 backdrop-blur-md text-white text-sm rounded-full">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="p-12 flex flex-col">
                  <motion.h3 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-4xl font-bold text-gray-900 mb-2"
                  >
                    {currentProject.companyName}
                  </motion.h3>
                  
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className={`text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r ${currentProject.gradient} mb-6`}
                  >
                    {currentProject.tagline}
                  </motion.p>
                  
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-gray-600 text-lg mb-8"
                  >
                    {currentProject.description}
                  </motion.p>
                  
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="grid grid-cols-3 gap-6 mb-8"
                  >
                    {currentProject.results.map((result, idx) => (
                      <div key={idx} className="text-center">
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br ${currentProject.gradient} text-white mb-2`}>
                          {result.icon}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{result.value}</div>
                        <div className="text-sm text-gray-600">{result.label}</div>
                      </div>
                    ))}
                  </motion.div>
                  
                  <motion.a
                    href={currentProject.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`mt-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full bg-gradient-to-r ${currentProject.gradient} hover:shadow-lg transition-all duration-300`}
                  >
                    Webseite besuchen
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-4">
            <button 
              className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={handlePrev}
              aria-label="Previous project"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </button>
            
            <div className="flex gap-2">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`transition-all duration-300 ${
                    index === activeIndex 
                      ? 'w-8 h-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full' 
                      : 'w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400'
                  }`}
                  aria-label={`Go to project ${index + 1}`}
                />
              ))}
            </div>
            
            <button 
              className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={handleNext}
              aria-label="Next project"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </button>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-32 text-center"
        >
          <p className="text-2xl text-gray-600 mb-4">
            Über <span className="font-bold text-gray-900">10+ erfolgreiche Projekte</span> in den letzten 5 Jahren.
          </p>
          <p className="text-lg text-gray-500">
            Jedes einzelne mit messbarem Impact und begeisterten Kunden.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;